import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { showApiError } from "@/lib/toast";
import { codeRequest } from "@/sideEffects/api";
import { useTranslation } from "react-i18next";
import { LoadingSmall } from "@/components/LoadingSmall";

const formSchema = z.object({
  email: z.string().email(),
});

type FormSchemaType = z.infer<typeof formSchema>;

type Props = { onOtpSent: (toEmail: string) => void };

export const EmailForm: React.FC<Props> = ({ onOtpSent }) => {
  const { t } = useTranslation();
  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormSchemaType) => {
      localStorage.removeItem("token");
      return codeRequest(values.email);
    },
    onSuccess: (result, values) => {
      if (result.data === "True") {
        onOtpSent(values.email);
      }
    },
    onError: showApiError,
  });

  function onSubmit(values: FormSchemaType) {
    mutation.mutate(values);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 w-full">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("user.email")}</FormLabel>
              <FormControl>
                <Input placeholder={t("user.email")} type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={mutation.isPending}>
          {t("login.button")} {mutation.isPending && <LoadingSmall />}
        </Button>
      </form>
    </Form>
  );
};
